import { useXcoreUtils } from "@robe/client/xcore-ui";
import { AspectRatio, Card, CardProps, Flex, Typography } from "@xcorejs/ui";
import { resolveSrcSet } from "utils";
import { InnovationValues } from "xcore/types";
import { Skeleton } from "@mui/joy";
import { useEffect, useRef, useState } from "react";

export type InnovationCardInnovationValuesFields = "name" | "intro" | "image";

export type InnovationCardValues = Pick<InnovationValues, "name" | "image" | "intro">;

type InnovationCardProps = {
  values: InnovationCardValues;
} & CardProps;

const InnovationCard = ({ values, ...props }: InnovationCardProps) => {
  const { stringify, image } = useXcoreUtils();
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const [showLoading, setShowLoading] = useState<boolean>(false); // Shows debounced loading if image is still being downloaded
  const timeUntilShowLoading = 400; // in ms

  const imgRef = useRef<HTMLImageElement | null>(null);

  const name = stringify(values.name);
  const imgUrl = values.image ? image(values.image, { width: 410, height: 230 }) : null;
  const retinaImgUrl = values.image ? image(values.image, { width: 410, height: 230, enlargement: 2 }) : null;
  const description = stringify(values.intro);

  // Check on mount if the image is already loaded from cache
  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsImageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isImageLoading) {
      // Show loading with debounce
      const timeout = setTimeout(() => setShowLoading(true), timeUntilShowLoading);

      return () => clearTimeout(timeout);
    }

    setShowLoading(false); // Do not show loading if image is loaded
  }, [isImageLoading]);

  return (
    <Card
      variant="outline"
      media={
        <AspectRatio ratio={23 / 41} width="100%">
          {showLoading && <Skeleton style={{ position: "absolute" }} variant="rectangular" width={410} height={230} />}

          {imgUrl && (
            <img
              ref={imgRef}
              src={imgUrl}
              {...resolveSrcSet(imgUrl, retinaImgUrl)}
              alt={name}
              width="100%"
              height="100%"
              loading="lazy"
              onLoad={() => setIsImageLoading(false)}
            />
          )}
        </AspectRatio>
      }
      _media={{ justifyContent: "center" }}
      body={
        (description || name) && (
          <Flex flexDirection="column" position="relative" zIndex={1} alignItems="center" width="100%">
            <Typography variant="h4" fontSize="1.5rem" lineHeight="2rem" textAlign="center">
              {name}
            </Typography>
            {description && (
              <Typography mt="1rem" variant="p" fontSize="1.5rem" lineHeight="2rem" textAlign="center" wordBreak="break-word">
                {description}
              </Typography>
            )}
          </Flex>
        )
      }
      _body={{ padding: "2rem 3rem 3rem 3rem" }}
      transition="box-shadow 300ms, border 300ms"
      boxShadow="0 3px 10px 0 rgba(30, 30, 30, 0.25)"
      _hover={{ boxShadow: "0 0.6rem 1.5rem 0 rgba(30, 30, 30, 0.5)" }}
      border={0}
      maxWidth="100%"
      {...props}
    />
  );
};

export default InnovationCard;

export const innovationCardInnovationFields: InnovationCardInnovationValuesFields[] = ["name", "intro", "image"];
